import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getCurrentUser, subscribeToAuth } from '../lib/auth';
import { useLibrary, useReadingChallenge } from '../hooks/useLibrary';
import { useUserProfile } from '../hooks/useUserProfile';
import { IoCameraOutline } from 'react-icons/io5';
import {
  CurrentThoughtBlock, CurrentlyReadingBlock, ToKnowMeBlock,
  TopTropesBlock, ReadingStatsBlock, ReadingGoalBlock, JournalBlock,
  MyShopBlock, AskMeAboutBlock, SubstackBlock, HotTakeBlock,
  CustomShelfBlock, CustomLinksBlock, EventsBlock,
  ShowcaseBlock, RecommendMeBlock, AmazonStorefrontBlock, YouTubeBlock, 
  PodcastBlock
} from './ProfileBlocks';
import ShareButton from '../components/ShareButton';
import { useGuestGate } from '../hooks/useGuestGate';
import SignUpGate from '../components/SignUpGate';
import CSVUploadModal from '../components/CSVUploadModal';
import { submitTropeName, tagNameError } from '../lib/tropeUtils';

// ── useIsMobile ──────────────────────────────────────────────────────────────
// Below PHONE_LAYOUT_BELOW the profile uses the phone layout: one column,
// ▲▼ to reorder, the floating + to add. It used to switch at 768, and
// between 768 and 1023 the desktop layout's three columns, beside the 260px
// block palette, came out 132px to 217px wide (154px on an iPad held
// upright, 834). A book title broke a letter or two per line in them and
// block names ran under the pencil. At 1024 the columns are 217px, the
// narrowest any desktop column now gets.
//
// PublicProfile.jsx keeps 768 on purpose. A visitor's view has no palette,
// so its columns are 225px or wider at every width from 768 up.
const PHONE_LAYOUT_BELOW = 1024;

// How wide the phone layout's column, and its bottom sheets, may grow. On a
// phone this does nothing; on an iPad it stops each block stretching across
// the whole screen.
const PHONE_COLUMN_MAX = 640;

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < PHONE_LAYOUT_BELOW);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < PHONE_LAYOUT_BELOW);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

// ── Constants ────────────────────────────────────────────────────────────────
const PRESET_ARCHETYPES = [
  { emoji: '🌌', label: 'Lost in Other Worlds' },
  { emoji: '💕', label: '2% Water 98% Romance' },
  { emoji: '📱', label: 'Multiple Kindle Owner' },
  { emoji: '✈️', label: 'World Traveller via Books' },
  { emoji: '😱', label: 'Books Fear Me' },
  { emoji: '💡', label: 'Sleeps with Lights On' },
];

// The ✉️ pill used to be labelled Substack and wrote to `substack_url`, so
// readers who read the envelope as "email" typed their address into the
// newsletter field — which is how emails ended up on Reader's Corner cards.
// It is an email field now; Substack and Medium come only from the block.
const SOCIAL_PLATFORMS = [
  { key: 'instagram', label: 'Instagram', icon: '📷', color: '#e1306c', prefix: '@' },
  { key: 'tiktok',    label: 'TikTok',    icon: '🎵', color: '#010101', prefix: '@' },
  { key: 'twitter',   label: 'Twitter/X', icon: '𝕏', color: '#1da1f2', prefix: '@' },
  { key: 'email',     label: 'Email',     icon: '✉️', color: 'var(--neutral-600)', prefix: '', placeholder: 'you@example.com' },
];

const BLOCK_TYPES = [
  { type: 'current_thought',   emoji: '💭', name: 'Current Thought',           desc: 'Share what\'s on your reading mind right now' },
  { type: 'currently_reading', emoji: '📖', name: 'Currently Reading',          desc: 'Up to four books you\'re reading right now' },
  { type: 'to_know_me',        emoji: '💛', name: 'To Know Me is to Love Me',   desc: 'Your all-time favourite books' },
  { type: 'top_tropes',        emoji: '🏷️', name: 'My Top Tropes',             desc: 'Tropes that define your reading taste' },
  { type: 'reading_stats',     emoji: '📊', name: 'Reading Stats',              desc: 'Your reading analytics from Goodreads' },
  { type: 'my_shop',           emoji: '🛍️', name: 'My Shop',                   desc: 'Products you sell: candles, prints, kits' },
  // Anchored after my_shop rather than appended to the end of this array: the
  // end is the one spot two branches both pick, so it is the one spot they
  // collide. my_shop is also the nearest relative -- both are "somewhere else
  // I exist", and Etsy lives in that one.
  { type: 'custom_links',      emoji: '🔗', name: 'My Links',                  desc: 'Up to 3 links to places with no block of their own' },
  { type: 'events', emoji: '📍', name: 'My Events', desc: 'Events you\'re hosting, going to, or interested in' },
  { type: 'substack',          emoji: '✍️', name: 'Latest from my newsletter', desc: 'Your most recent Substack or Medium posts' },
  { type: 'ask_me_about',      emoji: '💼', name: 'Ask Me About',               desc: 'Services you offer as a bookish creator' },
  { type: 'hot_take',          emoji: '🔥', name: 'Hot Take',                   desc: 'Up to four hills to die on, one at a time' },
  { type: 'showcase',          emoji: '🎨', name: 'Showcase',                   desc: 'Your art, designs, or creative work' },
  { type: 'recommend_me',      emoji: '📚', name: 'If You Like What I Like',    desc: 'Active book recommendations with pitches' },
  { type: 'reading_goal',      emoji: '🎯', name: 'My Reading Goal',            desc: 'Your yearly reading progress bar' },
  { type: 'custom_shelf',      emoji: '📚', name: 'Custom Shelf',               desc: 'A themed shelf around any idea you want' },
  { type: 'amazon_storefront', emoji: '📦', name: 'Amazon Storefront',          desc: 'Showcase curated lists from your Amazon storefront' },
  { type: 'youtube', emoji: '🎬', name: 'My YouTube', desc: 'Showcase your latest YouTube videos' },
  { type: 'podcast', emoji: '🎙️', name: 'My Podcast', desc: 'Show your latest podcast episodes' },
];

// The journal is NOT in BLOCK_TYPES. Per review: it stopped being an optional
// block a reader adds from this palette -- it is a fixture every profile has,
// rendered unconditionally (see journalBlock below), so it must not also be
// offered here or a reader could add a second one. If you are tempted to put
// it back so the palette can show its emoji/name: don't -- that is what
// JOURNAL_DEF below is for, and BLOCK_TYPES being iterated is exactly the
// list this must stay off of.
// guestLine overrides the generic signed-out subtitle in BlockWrapper below.
// The generic one ("Sign up to fill this in...") is written for a block a
// reader assembles; the journal is a fixture every profile has, so being told
// to sign up and fill it in reads as an instruction rather than an invitation.
const JOURNAL_DEF = { type: 'journal', emoji: '📓', name: 'A Book That...', desc: 'Reflect on a book through a prompt', guestLine: 'Your reading journal' };

// How long "Removed … Undo" stays up after a block is removed. Long enough to
// notice a mis-tap on a phone and reach for it.
const UNDO_REMOVE_MS = 8000;

// The name the undo toast gives a removed block: a Custom Shelf by its own
// name, everything else by its palette name.
function removedBlockLabel(block) {
  if (block?.type === 'custom_shelf' && block.data?.shelf_name) return block.data.shelf_name;
  return BLOCK_TYPES.find(b => b.type === block?.type)?.name || 'Block';
}

// ─────────────────────────────────────────────────────────────────────────────
// ShareButton
// ─────────────────────────────────────────────────────────────────────────────


// ── Inline editable field ────────────────────────────────────────────────────
function InlineEdit({ value, placeholder, onSave, style, prefix = '', inputStyle = {} }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => { setVal(value || ''); }, [value]);
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  async function commit() {
    setEditing(false);
    if (val === value) return;
    const prev = value;
    try {
      const res = await onSave(val);
      if (res?.error) setVal(prev || ''); // revert on failure
    } catch {
      setVal(prev || '');
    }
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={val}
        onChange={e => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setVal(value || ''); setEditing(false); } }}
        style={{ background: 'transparent', border: 'none', borderBottom: '1.5px solid var(--feedback-warning)', outline: 'none', fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit', fontFamily: 'inherit', padding: '2px 4px', minWidth: 120, ...inputStyle }}
      />
    );
  }

  return (
    <span
      onClick={() => setEditing(true)}
      title="Click to edit"
      style={{ cursor: 'pointer', borderBottom: '1.5px dashed transparent', transition: 'border-color 0.15s', padding: '2px 4px', borderRadius: 'var(--radius-sm)', ...style }}
      onMouseEnter={e => e.currentTarget.style.borderBottomColor = '#d97706'}
      onMouseLeave={e => e.currentTarget.style.borderBottomColor = 'transparent'}
    >
      {val ? `${prefix}${val}` : <span style={{ color: 'var(--neutral-400)' }}>{placeholder}</span>}
    </span>
  );
}

// ── Drop Column (desktop only) ───────────────────────────────────────────────
function DropColumn({ col, colIdx, draggingPaletteType, dragBlock, onAddBlock, onRemoveBlock, onDragStartBlock, onDropBlock, onDropFromPalette, profile, onUpdate, onUpdateBlock, onOpenCsvModal, csvRefreshKey, hideTypes = [] }) {
  const [isDragOver, setIsDragOver] = useState(false);
  // Which block edge the block being dragged would land against, so a line can
  // be drawn there. Dragging with no visible target is why reordering felt
  // broken even once it worked: you cannot aim at something you cannot see.
  const [dropEdge, setDropEdge] = useState(null); // { blockId, below } | null
  // The orange dashed border and the drop line are cleared by this column's
  // own drop and dragleave handlers -- and a drop ON a block reaches neither:
  // the block's onDrop below stops propagation on purpose, and no dragleave
  // follows a drop. So after almost every real move both stayed on screen
  // until some unrelated re-render. The block's onDrop now clears them itself.
  // This listener covers the drag that never drops: cancelled with Escape, or
  // let go somewhere that is not a column. dragend is not enough on its own --
  // a block moved to another column is unmounted from under the drag, and a
  // dragend fired on a detached node never bubbles up to window.
  useEffect(() => {
    function clear() { setIsDragOver(false); setDropEdge(null); }
    window.addEventListener('dragend', clear);
    return () => window.removeEventListener('dragend', clear);
  }, []);
  function handleDragOver(e) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; setIsDragOver(true); }
  function handleDragLeave(e) {
    if (!e.currentTarget.contains(e.relatedTarget)) { setIsDragOver(false); setDropEdge(null); }
  }
  function handleDrop(e) {
    e.preventDefault(); setIsDragOver(false); setDropEdge(null);
    const paletteType = e.dataTransfer.getData('blockType');
    // Reached only when the drop missed every block — empty column, the
    // "drag a block here" hint, or the padding around them. No position to
    // infer, so it goes on the end.
    if (paletteType) onDropFromPalette(paletteType, colIdx, null);
    else if (dragBlock) onDropBlock(colIdx, null);
  }
  // dragover fires continuously, so only write state when the edge actually
  // changes — otherwise every mouse move re-renders the whole column.
  function markEdge(blockId, below) {
    setDropEdge(prev => (prev && prev.blockId === blockId && prev.below === below)
      ? prev : { blockId, below });
  }
  return (
    <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
      style={{ minHeight: 120, borderRadius: 'var(--radius-xl)', border: isDragOver ? '2px dashed var(--feedback-warning)' : '2px dashed transparent', background: isDragOver ? '#fffbeb' : 'transparent', transition: 'all 0.15s', padding: isDragOver ? 4 : 0 }}>
      {col.map(block => (
      hideTypes.includes(block.type) ? null :
      <BlockWrapper key={block.id} block={block} profile={profile}
  onRemove={onRemoveBlock}
  onDragStart={() => onDragStartBlock(block, colIdx)}
  onDragOver={e => {
    e.preventDefault();
    // Shown for a new block from the palette as well as a reorder. The
    // dragged payload cannot be read during dragover — the drag data store
    // is protected until drop — so a palette drag is recognised by
    // draggingPaletteType, which BlockPalette sets on dragstart.
    if (!dragBlock && !draggingPaletteType) return;
    const box = e.currentTarget.getBoundingClientRect();
    markEdge(block.id, e.clientY > box.top + box.height / 2);
  }}
  dropEdge={dropEdge && dropEdge.blockId === block.id
    ? (dropEdge.below ? 'bottom' : 'top') : null}
  onDrop={e => {
    e.preventDefault();
    // The drag is over, so the highlight goes now rather than waiting for
    // dragend -- see the effect at the top of this component.
    setIsDragOver(false); setDropEdge(null);
    // Nothing dropped on a block may bubble. The column's handler runs
    // second, inside the same event, still holding the pre-drop `columns`
    // and `dragBlock` — it would re-run the drop with no position and send
    // the block to the bottom, overwriting whatever happened here.
    e.stopPropagation();
    // Which side of this block the drop lands on decides where it goes: the
    // top half means "put it above me", the bottom half means "put it below
    // me". Without that, a drop can only ever mean one of the two and half
    // the moves a reader tries are impossible to express.
    const box = e.currentTarget.getBoundingClientRect();
    const below = e.clientY > box.top + box.height / 2;
    // A brand new block from the palette lands at a position too, rather
    // than always being appended to the end of the column.
    const paletteType = e.dataTransfer.getData('blockType');
    if (paletteType) {
      const at = col.findIndex(b => b.id === block.id);
      onDropFromPalette(paletteType, colIdx, below ? at + 1 : at);
      return;
    }
    onDropBlock(colIdx, block.id, below);
  }}
  isDragging={dragBlock?.blockId === block.id}
  onUpdate={onUpdate} onUpdateBlock={onUpdateBlock} onOpenCsvModal={onOpenCsvModal} csvRefreshKey={csvRefreshKey} />
      ))}
        <div data-onboarding={colIdx === 0 ? 'drop-col-0' : undefined} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-6)', padding: 'var(--space-14)', borderRadius: 14, border: `1.5px dashed ${isDragOver ? '#d97706' : '#e8e4dc'}`, background: isDragOver ? 'rgba(217,119,6,0.06)' : 'transparent', color: isDragOver ? 'var(--feedback-warning)' : 'var(--neutral-300)', fontSize: 'var(--font-size-sm)', transition: 'all 0.15s' }}>
        <span style={{ fontSize: 'var(--font-size-md)' }}>+</span>
        <span>{isDragOver ? 'Drop here' : 'Drag a block here'}</span>
      </div>
    </div>
  );
}

// ── Block Wrapper ─────────────────────────────────────────────────────────────
function BlockWrapper({ block, profile, onRemove, onDragStart, onDragOver, onDrop, isDragging, dropEdge, onUpdate, onUpdateBlock, onOpenCsvModal, csvRefreshKey, pinned = false }) {
  const isMobile = useIsMobile();
  const [hovered, setHovered] = useState(false);

  function renderBlock() {
    // Guest: show a visual placeholder so the layout feels real
    // but don't render block internals that require a user_id
    const isGuest = !profile?.user_id;
    if (isGuest) {
      // The fallback carries a desc as well as a name: the subtitle below
      // renders def.desc now, so a block type not in BLOCK_TYPES (an older
      // saved block, a type removed since) would print an empty line without
      // one.
      const def = block.type === 'journal'
        ? JOURNAL_DEF
        : BLOCK_TYPES.find(b => b.type === block.type) || { emoji: '📦', name: block.type, desc: 'Coming soon' };
      return (
        <div style={{ background: 'var(--brand-white)', borderRadius: 14, border: '1.5px dashed var(--brand-sand)', padding: '22px 16px', textAlign: 'center' }}>
          <div style={{ fontSize: 'var(--font-size-h3)', marginBottom: 'var(--space-sm)' }}>{def.emoji}</div>
          <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 4px' }}>{def.name}</p>
          {/* The block's own description, not a generic line. This said "Sign
              up to fill this in and share it with your readers" for every
              block, which tells a visitor nothing about the block they are
              looking at -- and "your readers" is author-portal language on a
              reader's profile. Every entry in BLOCK_TYPES already carries a
              desc written for it, shown in the palette on the left; it is the
              right sentence here too. guestLine overrides it where a block
              wants different wording signed out (the journal). */}
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', margin: '0 0 14px', lineHeight: 1.5 }}>
            {def.guestLine || def.desc}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
            <div style={{ height: 8, background: 'var(--neutral-100)', borderRadius: 'var(--radius-sm)' }} />
            <div style={{ height: 8, background: 'var(--neutral-100)', borderRadius: 'var(--radius-sm)', width: '75%', margin: '0 auto' }} />
            <div style={{ height: 8, background: 'var(--neutral-100)', borderRadius: 'var(--radius-sm)', width: '55%', margin: '0 auto' }} />
          </div>
        </div>
      );
    }

    const props = { profile, onUpdate, blockData: block.data };
    switch (block.type) {
      case 'current_thought':   return <CurrentThoughtBlock {...props} />;
      case 'currently_reading': return <CurrentlyReadingBlock {...props} />;
      case 'to_know_me':        return <ToKnowMeBlock {...props} />;
      case 'top_tropes':        return <TopTropesBlock {...props} />;
      case 'journal':           return <JournalBlock {...props} />;
    case 'reading_stats': return <ReadingStatsBlock profile={profile} onOpenCsvModal={onOpenCsvModal} refreshKey={csvRefreshKey} />;
      case 'reading_goal':      return <ReadingGoalBlock profile={profile} />;
      case 'my_shop':           return <MyShopBlock profile={profile} onUpdate={onUpdate} />;
      case 'ask_me_about':      return <AskMeAboutBlock profile={profile} onUpdate={onUpdate} />;
      case 'substack':          return <SubstackBlock profile={profile} onUpdate={onUpdate} />;
      case 'hot_take':          return <HotTakeBlock profile={profile} onUpdate={onUpdate} />;
      case 'custom_shelf':      return <CustomShelfBlock blockData={block.data} profile={profile} onUpdateBlock={data => onUpdateBlock(block.id, data)} />;
      case 'custom_links':      return <CustomLinksBlock blockData={block.data} blockId={block.id} profile={profile} onUpdateBlock={data => onUpdateBlock(block.id, data)} />;
      case 'events':            return <EventsBlock profile={profile} onUpdate={onUpdate} />;
      case 'showcase':          return <ShowcaseBlock profile={profile} onUpdate={onUpdate} />;
      case 'recommend_me':      return <RecommendMeBlock profile={profile} onUpdate={onUpdate} />;
      case 'amazon_storefront': return <AmazonStorefrontBlock profile={profile} onUpdate={onUpdate} />;
      case 'podcast': return <PodcastBlock profile={profile} onUpdate={onUpdate} />;
      case 'youtube': return <YouTubeBlock profile={profile} onUpdate={onUpdate} />;
      default:
        const def = BLOCK_TYPES.find(b => b.type === block.type) || { emoji: '📦', name: block.type, desc: 'Coming soon' };
        return (
          <div style={{ background: 'var(--brand-white)', borderRadius: 14, border: '1px solid var(--brand-sand)', padding: 'var(--space-lg)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-10)' }}>
              <span style={{ fontSize: 22 }}>{def.emoji}</span>
              <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--neutral-800)', margin: 'var(--space-none)' }}>{def.name}</p>
            </div>
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', fontStyle: 'italic', margin: 'var(--space-none)' }}>Coming soon</p>
          </div>
        );
    }
  }

  // On mobile: remove button always visible, no drag. Except the journal --
  // it is a fixture, not removable from here at all, on either path this
  // component covers (the pinned desktop slot or the mobile list). pinned
  // already gated dragging (see draggable/onDragStart/onDragOver/onDrop
  // below); it was never wired to the remove button, so the X ignored it.
  //
  // pinned only covers THIS component's two call sites. It is not what keeps
  // the journal out of the draggable columns -- DropColumn's own
  // hideTypes={['journal']} does that, and pinned is never passed there at
  // all. If a second DropColumn is ever added without that argument, the
  // journal becomes removable again from that path and this line will not
  // save it.
  const showRemove = !pinned && (isMobile || hovered);

  // The 12px gap under each block is paddingBottom, not marginBottom. Margin
  // sits outside the element, so that gap belonged to the column rather than to
  // either block: a drop aimed between two blocks fell through to the column's
  // handler and sent the block to the bottom. As padding it is part of the
  // block above, so the block lands where the gap looked like it pointed.
  return (
  <div
  id={`block-${block.id}`}
  className="block-wrapper"
  draggable={!isMobile && !pinned}
      onDragStart={!isMobile && !pinned ? onDragStart : undefined}
      onDragOver={!isMobile && !pinned ? onDragOver : undefined}
      onDrop={!isMobile && !pinned ? onDrop : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ paddingBottom: 'var(--space-md)', position: 'relative', opacity: isDragging ? 0.4 : 1, transition: 'opacity 0.15s', cursor: isMobile || pinned ? 'default' : (isDragging ? 'grabbing' : 'grab') }}
    >
      {/* Where the dragged block would land. Sits in the 12px gap, above the
          block for a top-half hover and below it for a bottom-half one, so the
          line is always drawn between the two blocks it would separate. */}
      {dropEdge && (
        <div
          data-no-print="true"
          style={{
            position: 'absolute', left: 0, right: 0, height: 3, borderRadius: 2,
            background: 'var(--feedback-warning)', zIndex: 11, pointerEvents: 'none',
            ...(dropEdge === 'top' ? { top: -7 } : { bottom: 5 }),
          }}
        />
      )}
      {/* Drag handle — desktop only.
          Always rendered rather than only on hover: readers reported there was
          no way to move blocks on desktop, when in fact dragging worked and
          nothing on screen said so. A handle that only appears once you are
          already hovering cannot tell you the feature exists.
          pointerEvents stays off so the grab lands on the draggable wrapper
          underneath rather than being swallowed here. */}
      {!isMobile && !pinned && (
        <div
          title="Drag to move this block"
          data-no-print="true"
          style={{ position: 'absolute', top: 6, left: 5, color: hovered ? 'var(--neutral-500)' : 'var(--neutral-400)', fontSize: 'var(--font-size-sm)', lineHeight: 1, zIndex: 10, pointerEvents: 'none', transition: 'color 0.15s' }}
        >⠿</div>
      )}
      {/* Remove button */}
      {showRemove && (
        <button onClick={() => onRemove(block.id)}
          style={{ position: 'absolute', top: 8, right: 8, width: 22, height: 22, borderRadius: 'var(--radius-circle)', background: '#fef2f2', border: '1px solid #fecaca', color: 'var(--feedback-error)', fontSize: 'var(--font-size-xs)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, zIndex: 10 }}>
          ✕
        </button>
      )}
      {renderBlock()}
    </div>
  );
}

// ── Block Palette (desktop sidebar) ──────────────────────────────────────────
function BlockPalette({ existingTypes, onDragStart }) {
  const [search, setSearch] = useState('');
  const filtered = BLOCK_TYPES.filter(b =>
    b.name.toLowerCase().includes(search.toLowerCase()) ||
    b.desc.toLowerCase().includes(search.toLowerCase())
  );
  return (
    <div style={{ width: 260, flexShrink: 0, background: 'var(--brand-white)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--brand-sand)', display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 200px)', position: 'sticky', top: 20 }}>
      <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid var(--neutral-100)' }}>
        <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--neutral-800)', letterSpacing: 0.5, textTransform: 'uppercase', margin: '0 0 10px' }}>Blocks</p>
        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', margin: '0 0 10px', lineHeight: 1.4 }}>
          Drag any block into a column to add it. To move one you have already added, drag it by the <span style={{ color: 'var(--neutral-500)' }}>⠿</span> in its corner.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', background: 'var(--brand-paper)', borderRadius: 'var(--radius-md)', padding: '6px 10px', border: '1px solid var(--brand-sand)' }}>
          <span style={{ color: 'var(--neutral-400)', fontSize: 'var(--font-size-sm)' }}>🔍</span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..."
            style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-800)' }} />
        </div>
      </div>
      <div style={{ overflowY: 'auto', padding: 'var(--space-sm)', scrollbarWidth: 'none', flex: 1 }}>
        {filtered.map(b => {
          const alreadyAdded = existingTypes.filter(t => t === b.type).length;
          const isMultiple   = b.type === 'custom_shelf';
          const disabled     = alreadyAdded > 0 && !isMultiple;
          return (
           <div key={b.type} draggable={!disabled}
              onDragStart={e => { if (disabled) return; e.dataTransfer.setData('blockType', b.type); e.dataTransfer.effectAllowed = 'copy'; onDragStart(b.type); }}
              data-onboarding={!disabled ? 'palette-first-item' : undefined}
              style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)', padding: '10px 10px', borderRadius: 'var(--radius-10)', border: '1px solid #f0f0ef', background: disabled ? 'var(--brand-paper)' : 'var(--brand-white)', cursor: disabled ? 'not-allowed' : 'grab', marginBottom: 'var(--space-6)', opacity: disabled ? 0.4 : 1, transition: 'all 0.15s', userSelect: 'none' }}
              onMouseEnter={e => { if (!disabled) { e.currentTarget.style.background = '#fef3c7'; e.currentTarget.style.borderColor = '#fde68a'; } }}
              onMouseLeave={e => { e.currentTarget.style.background = disabled ? '#faf8f5' : '#fff'; e.currentTarget.style.borderColor = '#f0f0ef'; }}>
              <span style={{ fontSize: 'var(--font-size-xxl)', flexShrink: 0 }}>{b.emoji}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--neutral-800)', margin: 'var(--space-none)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.name}</p>
                <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', margin: '1px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.desc}</p>
              </div>
              {!disabled && <span style={{ fontSize: 'var(--font-size-md)', color: 'var(--neutral-300)', flexShrink: 0 }}>⠿</span>}
              {disabled  && <span style={{ fontSize: 'var(--font-size-tiny)',  color: 'var(--neutral-400)', fontStyle: 'italic', flexShrink: 0 }}>Added</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Mobile Block Picker (bottom sheet) ───────────────────────────────────────
function MobileBlockPicker({ existingTypes, addedBlocks, onAdd, onMove, onClose }) {
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('add');
  const filtered = BLOCK_TYPES.filter(b =>
    (b.name.toLowerCase().includes(search.toLowerCase()) ||
     b.desc.toLowerCase().includes(search.toLowerCase()))
  );
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 400, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} onClick={onClose} />
      <div style={{ position: 'relative', width: '100%', maxWidth: PHONE_COLUMN_MAX, background: 'var(--neutral-800)', borderRadius: '20px 20px 0 0', maxHeight: '80vh', display: 'flex', flexDirection: 'column', animation: 'slideUp 0.2s ease' }}>
        <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-md)' }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-lg)', fontWeight: 700, color: 'var(--brand-paper)', margin: 'var(--space-none)' }}>Blocks</h3>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--neutral-500)', cursor: 'pointer', fontSize: 'var(--font-size-xl)', lineHeight: 1 }}>✕</button>
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-6)', marginBottom: 'var(--space-md)' }}>
            {['add', 'reorder'].map(t => (
              <button key={t} onClick={() => setTab(t)}
                style={{ flex: 1, padding: '7px', borderRadius: 'var(--radius-md)', border: 'none', background: tab === t ? 'var(--brand-gold)' : 'rgba(255,255,255,0.08)', color: tab === t ? 'var(--neutral-800)' : 'var(--neutral-400)', fontSize: 'var(--font-size-sm)', fontWeight: 700, cursor: 'pointer' }}>
                {t === 'add' ? '+ Add Block' : '↕ Reorder'}
              </button>
            ))}
          </div>
          {tab === 'add' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', background: 'rgba(255,255,255,0.06)', borderRadius: 'var(--radius-md)', padding: '6px 10px' }}>
              <span style={{ color: 'var(--neutral-500)', fontSize: 'var(--font-size-sm)' }}>🔍</span>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search blocks..."
                style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 'var(--font-size-sm)', color: 'var(--brand-paper)' }} />
            </div>
          )}
        </div>
        <div style={{ overflowY: 'auto', padding: '8px 16px 24px', flex: 1 }}>
          {tab === 'add' ? filtered.map(b => {
            const alreadyAdded = existingTypes.filter(t => t === b.type).length;
            const isMultiple   = b.type === 'custom_shelf';
            const disabled     = alreadyAdded > 0 && !isMultiple;
            return (
              <button key={b.type} disabled={disabled}
                onClick={() => { if (!disabled) { onAdd(b.type); onClose(); } }}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '11px 14px', borderRadius: 'var(--radius-10)', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', cursor: disabled ? 'not-allowed' : 'pointer', textAlign: 'left', marginBottom: 'var(--space-6)', opacity: disabled ? 0.4 : 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)' }}>
                  <span style={{ fontSize: 'var(--font-size-xxl)' }}>{b.emoji}</span>
                  <div>
                    <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--brand-paper)', margin: 'var(--space-none)' }}>{b.name}</p>
                    <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-500)', margin: '1px 0 0' }}>{b.desc}</p>
                  </div>
                </div>
                <span style={{ fontSize: 'var(--font-size-md)', color: disabled ? 'var(--neutral-500)' : 'var(--brand-gold)', flexShrink: 0 }}>
                  {disabled ? '✓' : '+'}
                </span>
              </button>
            );
          }) : addedBlocks.length === 0 ? (
            <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', textAlign: 'center', padding: '20px 0' }}>No blocks added yet.</p>
          ) : addedBlocks.map((block, idx) => {
            const meta = block.type === 'journal'
              ? JOURNAL_DEF
              : BLOCK_TYPES.find(b => b.type === block.type) || { emoji: '📦', name: block.type };
            return (
              <div key={block.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)', padding: '10px 14px', borderRadius: 'var(--radius-10)', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', marginBottom: 'var(--space-6)' }}>
                <span style={{ fontSize: 'var(--font-size-xl)' }}>{meta.emoji}</span>
                <p style={{ flex: 1, fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--brand-paper)', margin: 'var(--space-none)' }}>{meta.name}</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <button onClick={() => onMove(block.id, -1)} disabled={idx === 0}
                    style={{ width: 26, height: 26, borderRadius: 6, border: 'none', background: idx === 0 ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.12)', color: idx === 0 ? 'var(--neutral-600)' : 'var(--brand-paper)', fontSize: 'var(--font-size-xs)', cursor: idx === 0 ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>▲</button>
                  <button onClick={() => onMove(block.id, 1)} disabled={idx === addedBlocks.length - 1}
                    style={{ width: 26, height: 26, borderRadius: 6, border: 'none', background: idx === addedBlocks.length - 1 ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.12)', color: idx === addedBlocks.length - 1 ? 'var(--neutral-600)' : 'var(--brand-paper)', fontSize: 'var(--font-size-xs)', cursor: idx === addedBlocks.length - 1 ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>▼</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

  // ── Archetype Modal ──────────────────────────────────────────────────────────
  // ── Archetype Modal ──────────────────────────────────────────────────────────
  const EMOJI_OPTIONS = [
  '✨','🌙','🌟','⚡','🔥','💫','🌊','🍂','🌸','🌿',
  '🦋','🐉','🦄','🐺','🦁','🐍','🦅','🐙','🌙','🎭',
  '💀','👑','🗡️','🔮','📚','🎨','🎵','🌙','🍵','☕',
  '🕯️','🏹','⚔️','🌋','🌌','🎪','🎠','🧙','🧛','🧝',
];

function EmojiPicker({ selected, onSelect }) {
  return (
    <div style={{ marginTop: 'var(--space-10)' }}>
      <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', margin: '0 0 8px' }}>Pick an Emoji</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-6)' }}>
        {EMOJI_OPTIONS.map((em, i) => (
          <button
            key={i}
            onClick={e => { e.stopPropagation(); onSelect(em); }}
            style={{
              width: 36, height: 36, borderRadius: 'var(--radius-md)', border: selected === em ? '2px solid var(--feedback-warning)' : '1px solid var(--brand-sand)',
              background: selected === em ? 'var(--feedback-warning-bg)' : 'var(--brand-white)',
              cursor: 'pointer', fontSize: 'var(--font-size-xl)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.1s',
            }}
          >{em}</button>
        ))}
      </div>
    </div>
  );
}

function ArchetypeModal({ profile, onSave, onClose }) {
  const isMobile = useIsMobile();
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [isCustom, setIsCustom]             = useState(false);
  const [customLabel, setCustomLabel]       = useState('');
  const [customEmoji, setCustomEmoji]       = useState('✨');

  const canSave = isCustom ? customLabel.trim().length > 0 : selectedPreset !== null;
  const preview = isCustom
    ? { emoji: customEmoji, label: customLabel }
    : selectedPreset !== null ? PRESET_ARCHETYPES[selectedPreset] : null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', zIndex: 1000, padding: isMobile ? 0 : 20 }} onClick={onClose}>
      <div style={{ background: 'var(--brand-paper)', borderRadius: isMobile ? '20px 20px 0 0' : 20, maxWidth: isMobile ? PHONE_COLUMN_MAX : 480, width: '100%', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', maxHeight: isMobile ? '92vh' : '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ background: 'var(--neutral-800)', padding: '22px 28px 20px', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, var(--feedback-warning), #f59e0b, var(--feedback-warning))' }} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--feedback-warning)', letterSpacing: 2, textTransform: 'uppercase', fontWeight: 600, margin: '0 0 4px' }}>Customize Your</p>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--brand-paper)', margin: 'var(--space-none)' }}>Reader Archetype</h2>
            </div>
            <span style={{ fontSize: 'var(--font-size-h2)' }}>🐉</span>
          </div>
          <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', margin: '8px 0 0' }}>Choose a preset or write your own</p>
        </div>

        <div style={{ padding: '20px 28px 24px' }}>

          {/* Presets grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-sm)', marginBottom: 'var(--space-lg)' }}>
            {PRESET_ARCHETYPES.map((p, i) => {
              const active = !isCustom && selectedPreset === i;
              return (
                <button key={i} onClick={() => { setSelectedPreset(i); setIsCustom(false); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)', padding: '11px 13px', borderRadius: 'var(--radius-lg)', cursor: 'pointer', border: active ? '2px solid var(--feedback-warning)' : '1px solid var(--brand-sand)', background: active ? 'var(--feedback-warning-bg)' : 'var(--brand-white)', textAlign: 'left', transition: 'all 0.15s' }}>
                  <span style={{ fontSize: 'var(--font-size-xxl)' }}>{p.emoji}</span>
                  <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: active ? 700 : 500, color: active ? 'var(--feedback-warning-ink)' : 'var(--neutral-700)', margin: 'var(--space-none)' }}>{p.label}</p>
                </button>
              );
            })}
          </div>

          {/* Divider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', margin: '0 0 14px' }}>
            <div style={{ flex: 1, height: 1, background: 'var(--brand-sand)' }} />
            <span style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', fontWeight: 600, letterSpacing: 1 }}>OR</span>
            <div style={{ flex: 1, height: 1, background: 'var(--brand-sand)' }} />
          </div>

          {/* Custom section */}
          <div
            onClick={() => { setIsCustom(true); setSelectedPreset(null); }}
            style={{ padding: 'var(--space-14)', borderRadius: 'var(--radius-lg)', cursor: 'pointer', border: isCustom ? '2px solid var(--feedback-warning)' : '1px dashed var(--neutral-300)', background: isCustom ? '#fffbeb' : 'var(--brand-paper)', transition: 'all 0.15s', marginBottom: 'var(--space-14)' }}
          >
            {isCustom ? (
              <div onClick={e => e.stopPropagation()}>
                {/* Emoji + label row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)', marginBottom: 'var(--space-md)' }}>
                  {/* Selected emoji preview */}
                  <div style={{ width: 44, height: 44, borderRadius: 'var(--radius-10)', background: 'var(--feedback-warning-bg)', border: '1px solid var(--feedback-warning-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-h4)', flexShrink: 0 }}>
                    {customEmoji}
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', margin: '0 0 4px' }}>Archetype Name</p>
                    <input
                      autoFocus
                      value={customLabel}
                      onChange={e => setCustomLabel(e.target.value)}
                      placeholder="e.g. The Night Owl Reader"
                      style={{ width: '100%', border: 'none', borderBottom: '1px solid var(--brand-sand)', outline: 'none', fontSize: 'var(--font-size-md)', fontWeight: 600, color: 'var(--neutral-800)', background: 'transparent', fontFamily: 'var(--font-display)', paddingBottom: 'var(--space-xs)' }}
                    />
                  </div>
                </div>

                {/* Emoji picker */}
                <EmojiPicker selected={customEmoji} onSelect={setCustomEmoji} />
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)' }}>
                <div style={{ width: 34, height: 34, borderRadius: 'var(--radius-10)', background: 'var(--neutral-100)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: '1px solid var(--brand-sand)' }}>
                  <span style={{ fontSize: 'var(--font-size-lg)', color: 'var(--neutral-400)' }}>+</span>
                </div>
                <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-400)', fontWeight: 500, margin: 'var(--space-none)' }}>Create your own</p>
              </div>
            )}
          </div>

          {/* Preview */}
          {preview && (
            <div style={{ padding: '12px 16px', background: 'var(--neutral-800)', borderRadius: 'var(--radius-lg)', marginBottom: 'var(--space-14)' }}>
              <p style={{ fontSize: 'var(--font-size-micro)', color: 'var(--neutral-500)', letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 600, margin: '0 0 6px', textAlign: 'center' }}>Preview</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)' }}>
                <span style={{ fontSize: 'var(--font-size-h4)' }}>{preview.emoji}</span>
                <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--brand-paper)', fontWeight: 700, fontFamily: 'var(--font-display)', margin: 'var(--space-none)' }}>{preview.label}</p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 'var(--space-10)' }}>
            <button onClick={onClose} style={{ flex: 1, padding: '11px', borderRadius: 'var(--radius-10)', border: '1px solid var(--brand-sand)', background: 'var(--brand-white)', fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--neutral-600)', cursor: 'pointer' }}>
              Cancel
            </button>
            <button
              disabled={!canSave}
              onClick={() => onSave(isCustom ? { emoji: customEmoji, label: customLabel } : PRESET_ARCHETYPES[selectedPreset])}
              style={{ flex: 1, padding: '11px', borderRadius: 'var(--radius-10)', border: 'none', background: canSave ? 'var(--neutral-800)' : 'var(--neutral-300)', fontSize: 'var(--font-size-base)', fontWeight: 600, color: canSave ? 'var(--brand-paper)' : 'var(--neutral-400)', cursor: canSave ? 'pointer' : 'default', transition: 'all 0.15s' }}
            >
              Save Archetype
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Profile Onboarding Hint ──────────────────────────────────────────────────
// ── Profile Onboarding Hint ──────────────────────────────────────────────────
// ── Profile Onboarding Hint ──────────────────────────────────────────────────
function ProfileOnboardingHint({ userId }) {
  const storageKey = `reanlo_profile_onboarding_seen_${userId}`;
  const [visible, setVisible] = useState(() => !localStorage.getItem(storageKey));
  const [mounted, setMounted] = useState(false);

  // Desktop animation state
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
  const [ghostPos,  setGhostPos]  = useState({ x: 0, y: 0, opacity: 0 });
  const [dropGlow,  setDropGlow]  = useState(false);
  const [phase, setPhase] = useState('idle');

  const timers = useRef([]);
  function later(fn, ms) { const t = setTimeout(fn, ms); timers.current.push(t); return t; }

  function dismiss() {
    localStorage.setItem(storageKey, '1');
    setVisible(false);
    timers.current.forEach(clearTimeout);
  }

  // ── Desktop cycle ──────────────────────────────────────────────────────────
  function runDesktopCycle() {
    const palEl  = document.querySelector('[data-onboarding="palette-first-item"]');
    const dropEl = document.querySelector('[data-onboarding="drop-col-0"]');
    if (!palEl || !dropEl) return;

    const pal  = palEl.getBoundingClientRect();
    const drop = dropEl.getBoundingClientRect();
    const palCx  = pal.left  + pal.width  / 2;
    const palCy  = pal.top   + pal.height / 2;
    const dropCx = drop.left + drop.width  / 2;
    const dropCy = drop.top  + drop.height / 2;

    setPhase('idle');
    setCursorPos({ x: palCx, y: palCy });
    setGhostPos({ x: palCx - 54, y: palCy - 27, opacity: 0 });
    setDropGlow(false);

    later(() => setPhase('grabbing'), 600);
    later(() => {
      setPhase('dragging');
      setGhostPos({ x: palCx - 54, y: palCy - 27, opacity: 0.88 });
    }, 1050);
    later(() => {
      setCursorPos({ x: dropCx, y: dropCy });
      setGhostPos({ x: dropCx - 54, y: dropCy - 27, opacity: 0.88 });
    }, 1200);
    later(() => setDropGlow(true), 1900);
    later(() => {
      setPhase('dropping');
      setGhostPos(prev => ({ ...prev, opacity: 0 }));
    }, 2300);
    later(() => { setPhase('released'); setDropGlow(false); }, 2700);
    later(() => runDesktopCycle(), 4200);
  }

  useEffect(() => {
    if (!visible) return;
    const t = setTimeout(() => {
      setMounted(true);
      runDesktopCycle();
    }, 400);
    return () => { clearTimeout(t); timers.current.forEach(clearTimeout); };
  }, [visible]);

  if (!visible || !mounted) return null;

  const isGrabbing = phase === 'grabbing' || phase === 'dragging';

  // ── Desktop portal ─────────────────────────────────────────────────────────
  return ReactDOM.createPortal(
    <>
      {/* Pulse ring around palette item */}
      {(() => {
        const palEl = document.querySelector('[data-onboarding="palette-first-item"]');
        if (!palEl) return null;
        const r = palEl.getBoundingClientRect();
        return (
          <div style={{
            position: 'fixed',
            left: r.left - 4, top: r.top - 4,
            width: r.width + 8, height: r.height + 8,
            borderRadius: 13,
            border: `2px solid ${isGrabbing ? '#d97706' : '#f59e0b'}`,
            boxShadow: isGrabbing ? '0 0 0 4px rgba(217,119,6,0.15)' : 'none',
            pointerEvents: 'none',
            zIndex: 991,
            transition: 'border-color 0.2s, box-shadow 0.2s',
          }} />
        );
      })()}

      {/* Drop zone glow */}
      {dropGlow && (() => {
        const dropEl = document.querySelector('[data-onboarding="drop-col-0"]');
        if (!dropEl) return null;
        const r = dropEl.getBoundingClientRect();
        return (
          <div style={{
            position: 'fixed',
            left: r.left - 2, top: r.top - 2,
            width: r.width + 4, height: r.height + 4,
            borderRadius: 15,
            border: '2px dashed var(--feedback-warning)',
            background: 'rgba(217,119,6,0.07)',
            boxShadow: '0 0 0 4px rgba(217,119,6,0.1)',
            pointerEvents: 'none',
            zIndex: 991,
          }} />
        );
      })()}

      {/* Ghost block */}
      {(phase === 'dragging' || phase === 'dropping') && (
        <div style={{
          position: 'fixed',
          left: ghostPos.x, top: ghostPos.y,
          width: 108, height: 54,
          borderRadius: 'var(--radius-10)',
          border: '1.5px solid var(--feedback-warning)',
          background: '#fffbeb',
          display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', padding: '0 12px',
          opacity: ghostPos.opacity,
          boxShadow: '0 8px 24px rgba(217,119,6,0.18)',
          pointerEvents: 'none',
          zIndex: 993,
          transition: 'left 0.72s cubic-bezier(0.4,0,0.2,1), top 0.72s cubic-bezier(0.4,0,0.2,1), opacity 0.3s',
        }}>
          <span style={{ fontSize: 'var(--font-size-xl)' }}>📖</span>
          <div>
            <div style={{ fontSize: 'var(--font-size-tiny)', fontWeight: 700, color: 'var(--feedback-warning-ink)', fontFamily: 'var(--font-heading)', lineHeight: 1.3 }}>Currently</div>
            <div style={{ fontSize: 'var(--font-size-tiny)', fontWeight: 700, color: 'var(--feedback-warning-ink)', fontFamily: 'var(--font-heading)', lineHeight: 1.3 }}>Reading</div>
          </div>
        </div>
      )}

      {/* Animated cursor */}
      <div style={{
        position: 'fixed',
        left: cursorPos.x, top: cursorPos.y,
        fontSize: 22, lineHeight: 1,
        pointerEvents: 'none',
        zIndex: 994,
        transform: 'translate(-4px, -4px)',
        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
        transition: 'left 0.72s cubic-bezier(0.4,0,0.2,1), top 0.72s cubic-bezier(0.4,0,0.2,1)',
      }}>
        <svg width="20" height="24" viewBox="0 0 20 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M2 2L2 18L6.5 13.5L9.5 21L12 20L9 13H15.5L2 2Z"
            fill={isGrabbing ? '#d97706' : '#292524'}
            stroke="#fff" strokeWidth="1.5" strokeLinejoin="round"/>
        </svg>
      </div>

      {/* Got it button — below first palette item */}
      {(() => {
        const palEl = document.querySelector('[data-onboarding="palette-first-item"]');
        if (!palEl) return null;
        const r = palEl.getBoundingClientRect();
        return (
          <div style={{ position: 'fixed', left: r.left, top: r.bottom + 10, zIndex: 992 }}>
            <button
              onClick={dismiss}
              style={{
                fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--neutral-500)',
                background: 'rgba(255,255,255,0.92)',
                border: '1px solid var(--brand-sand)',
                borderRadius: 'var(--radius-xxl)', padding: '4px 12px',
                cursor: 'pointer', backdropFilter: 'blur(4px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                transition: 'color 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.color = '#292524'}
              onMouseLeave={e => e.currentTarget.style.color = '#78716c'}
            >
              Got it ✕
            </button>
          </div>
        );
      })()}
    </>,
    document.body
  );
}

// ── Main Component ───────────────────────────────────────────────────────────
export default function ReaderProfile() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { library, loading: libraryLoading } = useLibrary();
  const { challenge } = useReadingChallenge();
  const { profile, loading: profileLoading, updateProfile, saveGuestDraft } = useUserProfile();
  const [csvRefreshKey, setCsvRefreshKey] = useState(0);
  const gate = useGuestGate('profile');

  const [user,                setUser]                = useState(null);
  const [uploadingAvatar,     setUploadingAvatar]     = useState(false);
  const [showArchetypeModal,  setShowArchetypeModal]  = useState(false);
  const [draggingPaletteType, setDraggingPaletteType] = useState(null);
  const [showCsvModal,        setShowCsvModal]        = useState(false);
  const [showMobileBlockPicker, setShowMobileBlockPicker] = useState(false);

  const captureRef      = useRef(null);
  const genreDropdownRef = useRef(null);

  const [editingSocial, setEditingSocial] = useState(null);
  const [socialInput,   setSocialInput]   = useState('');

  const [canonicalGenres,   setCanonicalGenres]   = useState([]);
  const [showGenreDropdown, setShowGenreDropdown] = useState(false);
  const [genreSearch,       setGenreSearch]       = useState('');
  const [addingCustomGenre, setAddingCustomGenre] = useState(false);
  const [genreWarning,      setGenreWarning]      = useState('');
  const [forcePrintMode, setForcePrintMode] = useState(false);

  useEffect(() => {
    async function fetchGenres() {
      const { data, error } = await supabase.from('tropes').select('canonical_name').eq('category', 'genre').eq('is_active', true).order('canonical_name');
      if (!error) setCanonicalGenres((data || []).map(t => t.canonical_name));
    }
    fetchGenres();
  }, []);

  useEffect(() => {
    if (!showGenreDropdown) return;
    function handleClickOutside(e) {
      if (genreDropdownRef.current && !genreDropdownRef.current.contains(e.target)) {
        setShowGenreDropdown(false); setGenreSearch('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showGenreDropdown]);

  const [columns,   setColumns]   = useState([[], [], []]);
  const [dragBlock, setDragBlock] = useState(null);
  // A drag cancelled with Escape, or dropped somewhere that is not a column,
  // never reaches a drop handler, so nothing cleared these and the block
  // stayed faded at 40% as though still mid-drag. dragend fires after drop,
  // so on a real drop the handlers have already read and cleared both; this
  // only matters for the drag that never dropped.
  useEffect(() => {
    function clear() { setDragBlock(null); setDraggingPaletteType(null); }
    window.addEventListener('dragend', clear);
    return () => window.removeEventListener('dragend', clear);
  }, []);
  const [saveStatus, setSaveStatus] = useState(null); // null | 'saving' | 'saved' | 'error'
const guestColumnsRef = useRef(null); // blocks built while guest, before login
// A guest's photo and custom genres, held until they have an account. The
// rest of a guest's header edits are held in useUserProfile (saveGuestDraft).
const guestAvatarRef = useRef(null);        // the File, uploaded at sign-in
const guestAvatarPreviewRef = useRef(null); // its blob: preview, revoked when replaced or saved
const guestCustomGenresRef = useRef([]);
// What the sign-in save needs to try again, when it failed (a failed photo
// upload or a failed write). Kept in a ref for the retry, mirrored in state
// for the chip.
const guestHeaderRetryRef = useRef(null);
const [guestHeaderFailed, setGuestHeaderFailed] = useState(false);
  const columnsInitialized = useRef(false);
  // See saveBlocks: what the database last confirmed, what last failed, and
  // which save is the latest.
  const lastSavedColumnsRef = useRef(null);
  const failedColumnsRef    = useRef(null);
  const saveSeqRef          = useRef(0);

  async function handleForcePrint() {
  setForcePrintMode(true);
  await new Promise(res => setTimeout(res, 600));
  window.scrollTo({ top: 0, behavior: 'instant' });
  await new Promise(res => setTimeout(res, 300));
  window.print();
  setTimeout(() => setForcePrintMode(false), 1500);
}

 useEffect(() => {
  async function init() {
    const u = await getCurrentUser();
    setUser(u);
  }
  init();

  // Migrate guest-built blocks to DB on sign-in
  // Shared listener rather than a fourth registration on this page — see the
  // comment in lib/auth.js on why each one risks an uncatchable AbortError.
  const unsubscribe = subscribeToAuth(async (event, session) => {
    if (event === 'SIGNED_IN' && session?.user) {
      setUser(session.user);
      // Blocks first, exactly as before: migrateGuestBlocks marks itself
      // pending straight away, so a block added meanwhile merges instead of
      // replacing the saved ones.
      if (guestColumnsRef.current) {
        const guestCols = guestColumnsRef.current;
        guestColumnsRef.current = null;
        const hasBlocks = guestCols.some(col => col.length > 0);
        // A throw here must not skip the header save below. migrateGuestBlocks
        // shows its own error chip for the blocks.
        try {
          if (hasBlocks) await migrateGuestBlocks(guestCols, session.user.id);
        } catch (err) {
          console.error('Guest block migration threw:', err?.message || err);
          setSaveStatus('error');
        }
      }
      // Then the header edits made as a guest. After the blocks, not beside
      // them: both saves send a username, and running them side by side could
      // put the old one back over the one the guest picked.
      const avatarFile = guestAvatarRef.current;
      guestAvatarRef.current = null;
      const customGenres = guestCustomGenresRef.current;
      guestCustomGenresRef.current = [];
      await saveGuestHeader({ user: session.user, avatarFile, avatarUrl: null, customGenres });
    }
  });

  return () => unsubscribe();
}, []);

  useEffect(() => {
    // A guest's profile is only what they edited this visit (useUserProfile),
    // with no saved blocks: building columns from it would clear the blocks
    // they have added on screen.
    if (!profile?.user_id) return;
    if (columnsInitialized.current) return;
    columnsInitialized.current = true;
    const blocks = profile.profile_blocks || [];
    const cols   = [[], [], []];
    blocks.forEach(b => {
      const col = b.column ?? 0;
      if (col >= 0 && col <= 2) cols[col].push(b);
    });
    cols.forEach(col => col.sort((a, b) => (a.order || 0) - (b.order || 0)));
    setColumns(cols);
    lastSavedColumnsRef.current = cols;
  }, [profile]);

  const [socialState, setSocialState] = useState({ instagram: '', tiktok: '', twitter: '', email: '' });
  useEffect(() => {
    if (profile) {
      setSocialState({
        instagram: profile.instagram_handle || '',
        tiktok:    profile.tiktok_handle    || '',
        twitter:   profile.twitter_handle   || '',
        email:     profile.contact_email    || '',
      });
    }
  }, [profile]);

  // ── Block operations ──────────────────────────────────────────────────────
// Returns whether the layout saved; onUpdateBlock hands that answer to the
// block that asked (ProfileBlocks.jsx, `saved`).
//
// The new layout goes on screen first, as before. If the save fails, the
// screen goes back to the last layout the database confirmed, so a removed or
// moved block does not look removed or moved when it is not (audit 0.4), and a
// link or shelf book that did not save leaves the list (audit 7.1). The one
// exception is the blocks a reader built as a guest, which stay on screen
// until they save (see migrateGuestBlocks). The error chip stays until the
// reader retries, and the retry re-sends what failed.
//
// Only the latest save may put the screen back. A later save was built from
// the screen this one put up, so it already carries this change: if it lands,
// the database has both.
async function saveBlocks(newColumns) {
  setColumns(newColumns);
  if (!user) {
    guestColumnsRef.current = newColumns; // store for migration on sign-in
    return true;
  }
  // Signed in, but the account's saved blocks haven't been read yet (see
  // migrateGuestBlocks). The screen holds only what was built as a guest, so
  // saving it as the layout would replace every saved block. Merge instead.
  const pending = pendingGuestRef.current;
  if (pending) return migrateGuestBlocks(newColumns, pending.userId);
  return persistColumns(newColumns);
}

async function persistColumns(newColumns) {
  const seq = ++saveSeqRef.current;
  setSaveStatus('saving');
  const flat = newColumns.flatMap((col, colIdx) =>
    col.map((b, order) => ({ ...b, column: colIdx, order }))
  );
  let error;
  try {
    ({ error } = await updateProfile({ profile_blocks: flat }));
  } catch (err) {
    error = err;
  }
  if (!error) {
    lastSavedColumnsRef.current = newColumns;
    failedColumnsRef.current = null;
    if (seq === saveSeqRef.current) {
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(s => (s === 'saved' ? null : s)), 2000);
    }
    return true;
  }
  console.error('saveBlocks failed:', error);
  if (seq === saveSeqRef.current) {
    failedColumnsRef.current = newColumns;
    if (lastSavedColumnsRef.current) setColumns(lastSavedColumnsRef.current);
    setSaveStatus('error');
  }
  return false;
}

// Blocks built while signed out, saved to the account the reader just signed
// in to.
//
// This used to save the guest's blocks AS the whole layout. For a returning
// reader that replaced every block they had saved, and the profile it sent
// came from a page that loaded signed out, so their username was replaced
// with a generated one too (audit 0.14). So: read what the account already
// has, and add the guest's blocks to it. A guest's block is an empty
// placeholder (BlockWrapper shows guests nothing else), so one whose type the
// account already has is dropped rather than doubled. A journal row is never
// carried across: the journal is a fixture that renders whether or not a row
// exists (journalBlock below), and it isn't in BLOCK_TYPES, so a guest can't
// add one.
//
// updateProfile reports failure by returning { error }, so the old try/catch
// also said "Saved" whether or not the blocks had been saved (audit 0.13).
// The read and the save are both checked now.
//
// Until the read succeeds, every layout save comes back through here (see
// saveBlocks), because the screen holds only the guest's blocks. Once it
// succeeds, the merged layout goes on screen, and it is also what a failed
// save puts back -- this merge's own, or any later edit's -- until a save
// lands. Putting the stored layout back instead would drop the guest's
// blocks from the screen, and so from every edit built from it after that:
// two failed saves and then one that worked lost them (found in review on
// #243). The retry chip is what says they haven't saved yet. Later edits are
// built from the merged screen, so they carry the guest's blocks, and from
// then on a failed save is an ordinary one: a retry sends the newest edit,
// not this merge again.
const pendingGuestRef  = useRef(null); // { guestCols, userId } until the saved blocks are read
const migrateSeqRef    = useRef(0);    // only the newest migration may write
const migrateLatestRef = useRef(null); // that migration's promise

function blocksToColumns(savedBlocks) {
  const cols = [[], [], []];
  savedBlocks.forEach(b => {
    const col = b.column ?? 0;
    if (col >= 0 && col <= 2) cols[col].push(b);
  });
  cols.forEach(col => col.sort((a, b) => (a.order || 0) - (b.order || 0)));
  return cols;
}

function mergeGuestBlocks(savedBlocks, guestCols) {
  const cols = blocksToColumns(savedBlocks);
  const have = new Set(savedBlocks.map(b => b.type));
  guestCols.forEach((col, i) => col.forEach(b => {
    if (b.type === 'journal' || have.has(b.type)) return;
    have.add(b.type);
    cols[Math.min(Math.max(i, 0), 2)].push(b);
  }));
  return cols;
}

function migrateGuestBlocks(guestCols, userId) {
  const run = runGuestMigration(guestCols, userId, ++migrateSeqRef.current);
  migrateLatestRef.current = run;
  return run;
}

async function runGuestMigration(guestCols, userId, seq) {
  pendingGuestRef.current = { guestCols, userId };
  setColumns(guestCols);
  setSaveStatus('saving');
  const { data: row, error } = await supabase
    .from('user_profiles')
    .select('profile_blocks')
    .eq('user_id', userId)
    .maybeSingle();
  // A newer edit started its own migration while this read was out. That
  // one carries this one's blocks too, so this one stops here, and answers
  // with whether that one saved.
  if (seq !== migrateSeqRef.current) return migrateLatestRef.current;
  if (error) {
    console.error('Guest block migration: reading the saved profile failed:', error.message);
    setSaveStatus('error');
    return false;
  }
  pendingGuestRef.current = null;
  const merged = mergeGuestBlocks(row?.profile_blocks || [], guestCols);
  lastSavedColumnsRef.current = merged;
  setColumns(merged);
  return persistColumns(merged);
}

function retryFailedSave() {
  const pending = pendingGuestRef.current;
  if (pending) { migrateGuestBlocks(pending.guestCols, pending.userId); return; }
  const failed = failedColumnsRef.current;
  if (!failed) { setSaveStatus(null); return; }
  saveBlocks(failed);
}

// `insertAt` is the position within the column, or null for the end. Adding
// from the palette by clicking has no position and appends; dropping onto an
// existing block passes the slot the drop indicator was pointing at.
async function addBlock(type, colIdx = 0, insertAt = null) {
  const newBlock = { id: `${type}_${Date.now()}`, type, column: colIdx, order: columns[colIdx].length };
  const newCols  = columns.map((col, i) => {
    if (i !== colIdx) return col;
    const next = [...col];
    if (insertAt === null || insertAt < 0 || insertAt > next.length) next.push(newBlock);
    else next.splice(insertAt, 0, newBlock);
    return next;
  });
  await saveBlocks(newCols);
  if (!user) gate.trackBlockBuilt();

  // On mobile, scroll the new block into view
  if (isMobile) {
    setTimeout(() => {
      const el = document.getElementById(`block-${newBlock.id}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
  }
}

  // One tap on ✕ removed a block for good, with no confirmation and no way
  // back, and on a phone the ✕ sits right next to the pencil. Most blocks keep
  // their content on the profile row, so adding one again brings it back, but
  // Custom Shelf and My Links keep theirs inside the block itself: removing
  // one deleted the shelf's books or the links (audit 0.3, 15.2).
  //
  // So a removal now offers Undo for UNDO_REMOVE_MS. Undo puts the same block
  // back, data and all, where it was, into the layout as it is by then rather
  // than the layout from before, so anything else changed meanwhile stays.
  const [undoRemove, setUndoRemove] = useState(null); // { block, col, index, label }
  const undoTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(undoTimerRef.current), []);

  async function removeBlock(blockId) {
    const col   = columns.findIndex(c => c.some(b => b.id === blockId));
    const index = col >= 0 ? columns[col].findIndex(b => b.id === blockId) : -1;
    const block = col >= 0 ? columns[col][index] : null;
    const ok = await saveBlocks(columns.map(c => c.filter(b => b.id !== blockId)));
    // A removal that did not save has nothing to undo. (saveBlocks answers
    // false for that once it reports failures; until then it answers nothing.)
    if (ok === false || !block) return;
    clearTimeout(undoTimerRef.current);
    setUndoRemove({ block, col, index, label: removedBlockLabel(block) });
    undoTimerRef.current = setTimeout(() => setUndoRemove(null), UNDO_REMOVE_MS);
  }

  async function undoRemoveBlock() {
    if (!undoRemove) return;
    clearTimeout(undoTimerRef.current);
    const { block, col, index } = undoRemove;
    setUndoRemove(null);
    const restored = columns.map((c, i) => {
      if (i !== col) return c;
      const at = Math.min(Math.max(index, 0), c.length);
      return [...c.slice(0, at), block, ...c.slice(at)];
    });
    await saveBlocks(restored);
  }

  async function moveBlockMobile(blockId, dir) {
    let newCols;
    setColumns(prev => {
      const flat = prev.flat().sort((a, b) => (a.order || 0) - (b.order || 0));
      const fromIdx = flat.findIndex(b => b.id === blockId);
      const toIdx = fromIdx + dir;
      if (toIdx < 0 || toIdx >= flat.length) return prev;
      [flat[fromIdx], flat[toIdx]] = [flat[toIdx], flat[fromIdx]];
      const cols = [[], [], []];
      flat.forEach((b, i) => {
        const col = b.column ?? 0;
        cols[col].push({ ...b, order: i });
      });
      newCols = cols;
      return cols;
    });
    // Through persistColumns like every other layout change, so a failed move
    // says so and the screen goes back rather than staying wrong.
    setTimeout(() => {
      if (!newCols || !user) return;
      persistColumns(newCols);
    }, 0);
  }

  // MERGES into the block's existing data rather than replacing it, and blocks
  // depend on that: a block sends only the keys it changed and expects the rest
  // of its `data` to survive. CustomLinksBlock is the clearest case — an
  // admin-removal notice lives in `data.removed_notice`, written by
  // admin_remove_custom_link, and it has to still be there after the owner
  // reorders or deletes a link, because that notice is the only way they are
  // ever told. Turning this into a replace breaks that block and every other
  // one silently. See the note on writeLinks in ProfileBlocks.jsx.
  async function updateBlockData(blockId, data) {
    const newCols = columns.map(col =>
      col.map(b => b.id === blockId ? { ...b, data: { ...(b.data || {}), ...data } } : b)
    );
    return saveBlocks(newCols);
  }

  function handleDragStart(block, colIdx) { setDragBlock({ blockId: block.id, fromCol: colIdx }); }

  // `below` says which half of the target block the drop landed on: false puts
  // the dragged block immediately above the target, true immediately below.
  function handleDrop(toCol, toBlockId, below = false) {
    if (!dragBlock) return;
    const { blockId, fromCol } = dragBlock;
    const newCols    = columns.map(col => [...col]);
    const fromColArr = newCols[fromCol];
    const blockIdx   = fromColArr.findIndex(b => b.id === blockId);
    if (blockIdx === -1) { setDragBlock(null); return; }

    // Every index is read BEFORE the block is spliced out, then corrected for
    // the hole the splice leaves behind. Reading the target's index afterwards
    // measures against an array that has already shifted, which silently
    // dropped the block a slot short of where it was aimed.
    let toIdx = toBlockId ? newCols[toCol].findIndex(b => b.id === toBlockId) : -1;
    if (toIdx !== -1 && below) toIdx += 1;
    if (fromCol === toCol && toIdx !== -1 && blockIdx < toIdx) toIdx -= 1;

    const [moved] = fromColArr.splice(blockIdx, 1);
    // toIdx === -1 means no target, or a target that is not in this column.
    // Either way the block belongs at the end — splice(-1) would quietly drop
    // it in second-to-last instead.
    if (toIdx === -1) newCols[toCol].push(moved);
    else newCols[toCol].splice(toIdx, 0, moved);

    setDragBlock(null);
    // A drop that resolves to where the block already sits is not a change;
    // skip the write rather than round-trip the whole layout for nothing.
    const unchanged = newCols.every((col, i) =>
      col.length === columns[i].length && col.every((b, j) => b.id === columns[i][j].id));
    if (unchanged) return;
    saveBlocks(newCols);
  }

  // Saves a guest's header edits once they have an account: the photo first
  // (a guest cannot upload), then the held fields (useUserProfile,
  // saveGuestDraft), then the custom genres that actually landed on the
  // profile go to the catalogue. Any failure keeps everything for the retry
  // chip rather than dropping it.
  async function saveGuestHeader({ user: u, avatarFile, avatarUrl, customGenres }) {
    let photoUrl = avatarUrl;
    if (avatarFile && !photoUrl) {
      const up = await uploadAvatar(avatarFile, u.id);
      if (!up.ok) {
        console.error('Saving the photo picked as a guest failed:', up.error);
        guestHeaderRetryRef.current = { user: u, avatarFile, avatarUrl: null, customGenres };
        setGuestHeaderFailed(true);
        return;
      }
      photoUrl = up.url;
    }
    let res;
    try {
      res = await saveGuestDraft(u, { avatarUrl: photoUrl });
    } catch (err) {
      res = { ok: false, error: err?.message || String(err) };
    }
    if (!res.ok) {
      console.error('Saving the profile edits made as a guest failed:', res.error);
      guestHeaderRetryRef.current = { user: u, avatarFile, avatarUrl: photoUrl, customGenres };
      setGuestHeaderFailed(true);
      return;
    }
    guestHeaderRetryRef.current = null;
    setGuestHeaderFailed(false);
    if (guestAvatarPreviewRef.current) {
      URL.revokeObjectURL(guestAvatarPreviewRef.current);
      guestAvatarPreviewRef.current = null;
    }
    // Only the names that are on the profile now: one removed before sign-up,
    // or cut by the three-genre cap when merged, is not sent.
    const landed = new Set(res.genres || []);
    customGenres.filter(g => landed.has(g)).forEach(g => {
      submitTropeName(g, 'genre').catch(err => console.error('Custom genre submit failed:', err.message));
    });
  }

  function retryGuestHeader() {
    const pending = guestHeaderRetryRef.current;
    if (!pending) { setGuestHeaderFailed(false); return; }
    saveGuestHeader(pending);
  }

  async function uploadAvatar(file, userId) {
    try {
      const ext  = file.name.split('.').pop();
      const name = `${userId}-${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from('avatars').upload(name, file, { upsert: true });
      if (error) return { ok: false, error: error.message };
      const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(name);
      return { ok: true, url: publicUrl };
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }

  async function handleAvatarUpload(e) {
    const file = e.target.files[0];
    if (!file || !file.type.startsWith('image/')) return;
    // Guest or signed in is decided by updateProfile itself (held or not),
    // the same check every other header edit uses, never by this page's
    // `user`, which is null for a moment after load even when signed in.
    // updateProfile refuses to store a blob: address for a signed-in reader.
    const preview = URL.createObjectURL(file);
    const res = await updateProfile({ avatar_url: preview });
    if (res.held) {
      // A guest cannot upload yet: the photo shows on this device now and is
      // uploaded once they have an account (saveGuestHeader).
      if (guestAvatarPreviewRef.current) URL.revokeObjectURL(guestAvatarPreviewRef.current);
      guestAvatarPreviewRef.current = preview;
      guestAvatarRef.current = file;
      return;
    }
    URL.revokeObjectURL(preview);
    setUploadingAvatar(true);
    try {
      const me = await getCurrentUser();
      if (!me) return;
      const up = await uploadAvatar(file, me.id);
      if (up.ok) await updateProfile({ avatar_url: up.url });
      else console.error('Avatar upload failed:', up.error);
    } finally { setUploadingAvatar(false); }
  }

  const userGenres      = profile?.interest_tags || [];
  const filteredGenres  = canonicalGenres.filter(g => !userGenres.includes(g) && g.toLowerCase().includes(genreSearch.toLowerCase()));
  const isCustomGenreInput = genreSearch.trim().length > 0 && !canonicalGenres.some(g => g.toLowerCase() === genreSearch.trim().toLowerCase()) && !userGenres.includes(genreSearch.trim());

  async function addGenre(g, isCustom = false) {
    if (userGenres.length >= 3) return;
    // Checked before updateProfile, not after, so an over-long name never
    // reaches the reader's own profile either. Refuse rather than truncate,
    // and leave their text alone so they can edit it down. See tagNameError
    // in tropeUtils for why this is not a maxLength.
    const problem = tagNameError(g);
    if (problem) { setGenreWarning(problem); return; }
    setGenreWarning('');
    const res = await updateProfile({ interest_tags: [...userGenres, g] });
    if (isCustom && res.held) {
      // A guest's name goes to the catalogue once they have an account, not
      // from an anonymous visit (the sign-in handler above).
      guestCustomGenresRef.current.push(g);
    } else if (isCustom && !res.error) {
      setAddingCustomGenre(true);
      // Route the custom genre through the matcher instead of creating the row
      // here. This used to insert an is_active false genre whenever the exact
      // string was missing, so "Romantasy " or "romantasy" became a second row
      // next to the real one. The genre is already on the reader's profile from
      // updateProfile above, so an outage costs the catalogue submission only.
      try {
        await submitTropeName(g, 'genre');
      } catch (err) { console.error('Custom genre submit failed:', err.message); }
      finally { setAddingCustomGenre(false); }
    }
    setShowGenreDropdown(false); setGenreSearch('');
  }

  async function removeGenre(g) {
    await updateProfile({ interest_tags: userGenres.filter(t => t !== g) });
  }

  async function saveSocial(key, val) {
    const map = { instagram: 'instagram_handle', tiktok: 'tiktok_handle', twitter: 'twitter_handle', email: 'contact_email' };

    // An email keeps its @; only handles get it stripped. The old code ran
    // `.replace('@','')` over everything, which turned jane@x.com into janex.com.
    const trimmed = val.trim();
    const cleaned = key === 'email' ? trimmed : trimmed.replace('@', '');

    // Clearing the field is how you remove it — don't store a blank.
    const next = cleaned || null;
    if (key === 'email' && cleaned && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleaned)) {
      setEditingSocial(null); // leave the saved value alone rather than storing junk
      return;
    }

    setSocialState(prev => ({ ...prev, [key]: cleaned }));
    setEditingSocial(null);
    await updateProfile({ [map[key]]: next });
  }

  const socialLinks   = socialState;
  const existingTypes = columns.flat().map(b => b.type);
  const allBlocks     = columns.flat().sort((a, b) => (a.order || 0) - (b.order || 0));

  // Per review on #162's build: the journal is the centrepiece block and
  // the worst-fitting one inside a 276px grid column (the tile is drawn for
  // 460). It gets its own full-width row above the grid instead.
  //
  // Per later review: it is not optional any more either. Every profile has
  // one, whether or not profile_blocks has a stored row for it yet -- a
  // reader who never touched the (now-removed) palette entry still gets the
  // fixture below, always first, always drawn. Where a real stored row DOES
  // exist (readers who added one under the old opt-in flow) its own
  // column/order are ignored -- position is the fixture's to set, not the
  // row's, any more. JournalBlock itself only needs `profile`, not this
  // object, so the synthesised id/column/order below exist purely to satisfy
  // BlockWrapper's bookkeeping (key, drag no-ops) and are never persisted.
  const journalBlock = allBlocks.find(b => b.type === 'journal')
    || { id: 'journal-fixture', type: 'journal', column: 0, order: -1 };
  const mobileBlocks = [journalBlock, ...allBlocks.filter(b => b.id !== journalBlock.id)];

  if (libraryLoading || profileLoading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--brand-paper)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 40, height: 40, border: '3px solid var(--brand-sand)', borderTopColor: 'var(--feedback-warning)', borderRadius: 'var(--radius-circle)', animation: 'spin 0.8s linear infinite' }} />
      </div>
    );
  }

  return (
    <div ref={captureRef} style={{ minHeight: '100vh', background: 'var(--brand-paper)', fontFamily: 'var(--font-body)' }}>

      {/* ── PROFILE HEADER ── */}
    {/* ── PROFILE HEADER ── */}
<div style={{ background: 'var(--brand-white)', borderBottom: '1px solid var(--brand-sand)' }}>
  <div style={{ maxWidth: 1100, margin: '0 auto', padding: isMobile ? '12px 16px' : '28px 32px 24px' }}>

    {/* ══ MOBILE: centered layout ══ */}
    {isMobile ? (
      <div style={{ position: 'relative', textAlign: 'center' }}>

        {/* Edit pencil — top-left */}
        <div style={{ position: 'absolute', top: 0, left: 0 }}>
          {/* placeholder for any edit action you want top-left */}
        </div>

        {/* Share button — top-right */}
        <div style={{ position: 'absolute', top: 0, right: 0 }}>
          {saveStatus && (
  <div
    // The error chip retries. It used to be a plain div that said "tap to
    // retry" and did nothing when tapped (audit 0.4).
    role={saveStatus === 'error' ? 'button' : 'status'}
    tabIndex={saveStatus === 'error' ? 0 : undefined}
    onClick={saveStatus === 'error' ? retryFailedSave : undefined}
    onKeyDown={saveStatus === 'error' ? (e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); retryFailedSave(); } }) : undefined}
    style={{
    cursor: saveStatus === 'error' ? 'pointer' : 'default',
    display: 'flex', alignItems: 'center', gap: 5,
    padding: '4px 10px', borderRadius: 'var(--radius-xxl)', fontSize: 'var(--font-size-xs)', fontWeight: 600,
    background: saveStatus === 'error' ? 'var(--feedback-error-bg)' : saveStatus === 'saving' ? 'var(--neutral-100)' : '#d1fae5',
    color: saveStatus === 'error' ? '#991b1b' : saveStatus === 'saving' ? 'var(--neutral-400)' : '#065f46',
    border: `1px solid ${saveStatus === 'error' ? '#fca5a5' : saveStatus === 'saving' ? '#e8e4dc' : '#a7f3d0'}`,
  }}>
    {saveStatus === 'saving' && <div style={{ width: 8, height: 8, borderRadius: 'var(--radius-circle)', border: '1.5px solid var(--neutral-400)', borderTopColor: 'transparent', animation: 'spin 0.7s linear infinite' }} />}
    {saveStatus === 'saved' && '✓'}
    {saveStatus === 'error' && '⚠'}
    {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved' : "Didn't save, tap to retry"}
  </div>
)}
          {guestHeaderFailed && (
  <div
    // The header edits made before sign-up, kept on screen until they save.
    role="button"
    tabIndex={0}
    onClick={retryGuestHeader}
    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); retryGuestHeader(); } }}
    style={{
      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, marginTop: 'var(--space-xs)',
      padding: '4px 10px', borderRadius: 'var(--radius-xxl)', fontSize: 'var(--font-size-xs)', fontWeight: 600,
      background: 'var(--feedback-error-bg)', color: '#991b1b', border: '1px solid #fca5a5',
    }}>
    ⚠ Your profile edits didn't save, tap to retry
  </div>
)}
          <ShareButton profile={profile} blocks={columns.flat()} />
        </div>

        {/* Avatar — centered */}
        <div style={{ display: 'inline-block', position: 'relative', marginBottom: 'var(--space-10)' }}>
          <div style={{ width: 72, height: 72, borderRadius: 'var(--radius-circle)', overflow: 'hidden', background: 'var(--neutral-100)', border: '2.5px solid var(--brand-gold)' }}>
            {profile?.avatar_url
              ? <img src={profile.avatar_url} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-h3)', color: 'var(--neutral-300)' }}>📷</div>
            }
          </div>
          <label htmlFor="avatar-upload" style={{ position: 'absolute', bottom: 0, right: 0, width: 22, height: 22, borderRadius: 'var(--radius-circle)', background: 'var(--brand-gold)', color: 'var(--brand-white)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.2)', fontSize: 'var(--font-size-xs)', border: '2px solid var(--brand-white)' }}>
            {uploadingAvatar ? '…' : <IoCameraOutline />}
          </label>
          <input id="avatar-upload" type="file" accept="image/*" onChange={handleAvatarUpload} style={{ display: 'none' }} />
        </div>

        {/* Username + location — centered row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-6)' }}>
          <InlineEdit
            value={profile?.username}
            placeholder="username"
            onSave={val => updateProfile({ username: val.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
            style={{ fontSize: 17, fontWeight: 800, color: 'var(--neutral-800)', fontFamily: 'var(--font-display)' }}
          />
          <InlineEdit
            value={profile?.location}
            placeholder="📍 location"
            prefix="📍 "
            onSave={val => updateProfile({ location: val })}
            style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)' }}
          />
        </div>

        {/* Archetype pill — centered */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-sm)' }}>
          {profile?.archetype_label ? (
            <button onClick={() => setShowArchetypeModal(true)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 12px', background: 'var(--neutral-800)', borderRadius: 'var(--radius-xxl)', border: 'none', cursor: 'pointer' }}>
              <span style={{ fontSize: 'var(--font-size-xs)' }}>{profile.archetype_emoji}</span>
              <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 600, color: 'var(--brand-paper)' }}>{profile.archetype_label}</span>
            </button>
          ) : (
            <button onClick={() => setShowArchetypeModal(true)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 12px', background: 'transparent', borderRadius: 'var(--radius-xxl)', border: '1.5px dashed var(--neutral-300)', cursor: 'pointer', fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)' }}>
              + Archetype
            </button>
          )}
        </div>

        {/* Genre pills — centered */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, flexWrap: 'wrap', marginBottom: 7, position: 'relative' }}>
          {userGenres.map(g => (
            <span key={g} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-xs)', padding: '3px 10px', background: 'var(--brand-cream)', color: 'var(--feedback-warning-ink)', border: '1px solid var(--feedback-warning-border)', borderRadius: 'var(--radius-xxl)', fontSize: 'var(--font-size-xxs)', fontWeight: 500 }}>
              {g}
              <span onClick={() => removeGenre(g)} style={{ cursor: 'pointer', opacity: 0.5, fontSize: 'var(--font-size-tiny)', marginLeft: 'var(--space-xxs)' }}>×</span>
            </span>
          ))}
          {userGenres.length < 3 && (
            <button onClick={() => setShowGenreDropdown(!showGenreDropdown)}
              style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 10px', background: 'transparent', border: '1.5px dashed var(--feedback-warning)', borderRadius: 'var(--radius-xxl)', fontSize: 'var(--font-size-xxs)', color: 'var(--feedback-warning)', fontWeight: 600, cursor: 'pointer' }}>
              +
            </button>
          )}
          {showGenreDropdown && (
            <div ref={genreDropdownRef} style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', zIndex: 100, background: 'var(--brand-white)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--brand-sand)', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', width: 280, marginTop: 'var(--space-6)' }}>
              <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--neutral-100)' }}>
                <input value={genreSearch} onChange={e => { setGenreSearch(e.target.value); if (genreWarning) setGenreWarning(''); }}
                  placeholder="Search or type a new genre..." autoFocus
                  style={{ width: '100%', border: 'none', outline: 'none', fontSize: 'var(--font-size-base)', color: 'var(--neutral-800)' }} />
              </div>
              <div style={{ maxHeight: 200, overflowY: 'auto', scrollbarWidth: 'none', padding: '6px 0' }}>
                {canonicalGenres.length === 0 ? (
                  <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', padding: '10px 14px', margin: 'var(--space-none)', textAlign: 'center' }}>Loading genres...</p>
                ) : filteredGenres.length > 0 ? (
                  filteredGenres.map(g => (
                    <button key={g} onClick={() => addGenre(g, false)}
                      style={{ display: 'block', width: '100%', padding: '8px 14px', background: 'none', border: 'none', textAlign: 'left', fontSize: 'var(--font-size-base)', color: 'var(--neutral-800)', cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#fef3c7'}
                      onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                      {g}
                    </button>
                  ))
                ) : genreSearch.trim() && !isCustomGenreInput ? (
                  <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', padding: '10px 14px', margin: 'var(--space-none)', fontStyle: 'italic' }}>Already added to your profile</p>
                ) : !genreSearch.trim() ? (
                  <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', padding: '10px 14px', margin: 'var(--space-none)' }}>Start typing to search genres</p>
                ) : null}
              </div>
              {isCustomGenreInput && (
                <div style={{ padding: '10px 12px', borderTop: '1px solid var(--neutral-100)', background: 'var(--brand-paper)', borderRadius: '0 0 12px 12px' }}>
                  <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', margin: '0 0 6px', lineHeight: 1.4 }}>"{genreSearch.trim()}" isn't in our list yet</p>
                  <button onClick={() => addGenre(genreSearch.trim(), true)} disabled={addingCustomGenre}
                    style={{ width: '100%', padding: '8px 12px', background: addingCustomGenre ? 'var(--brand-sand)' : 'var(--neutral-800)', color: addingCustomGenre ? 'var(--neutral-400)' : 'var(--brand-paper)', border: 'none', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', fontWeight: 600, cursor: addingCustomGenre ? 'default' : 'pointer' }}>
                    {addingCustomGenre ? 'Adding...' : `+ Add "${genreSearch.trim()}"`}
                  </button>
                  {genreWarning && (
                    <p role="status" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--feedback-warning-ink)', margin: '8px 0 0', lineHeight: 1.4 }}>
                      {genreWarning}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Social pills — centered */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, flexWrap: 'wrap' }}>
          {SOCIAL_PLATFORMS.map(platform => {
            const val       = socialLinks[platform.key];
            const isEditing = editingSocial === platform.key;
            if (!val && !isEditing) {
              return (
                <button key={platform.key} onClick={() => { setEditingSocial(platform.key); setSocialInput(''); }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '3px 9px', background: 'transparent', border: '1px dashed var(--brand-sand)', borderRadius: 'var(--radius-xxl)', fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', cursor: 'pointer' }}>
                  {platform.icon}
                </button>
              );
            }
            if (isEditing) {
              return (
                <div key={platform.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', background: 'var(--feedback-warning-bg)', border: '1.5px solid var(--feedback-warning-border)', borderRadius: 'var(--radius-xxl)' }}>
                  <span style={{ fontSize: 'var(--font-size-xs)' }}>{platform.icon}</span>
                  <input autoFocus value={socialInput} onChange={e => setSocialInput(e.target.value)}
                    onBlur={() => saveSocial(platform.key, socialInput)}
                    onKeyDown={e => { if (e.key === 'Enter') saveSocial(platform.key, socialInput); if (e.key === 'Escape') setEditingSocial(null); }}
                    placeholder={platform.placeholder || "@handle"}
                    style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 'var(--font-size-xs)', color: 'var(--feedback-warning-ink)', width: platform.key === 'email' ? 150 : 80 }} />
                </div>
              );
            }
            return (
              <div key={platform.key} onClick={() => { setEditingSocial(platform.key); setSocialInput(val); }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-xs)', padding: '3px 9px', background: platform.key === 'instagram' ? 'linear-gradient(135deg, #833ab4, #fd1d1d, #fcb045)' : platform.key === 'tiktok' ? 'var(--brand-black)' : 'var(--neutral-100)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-xxl)', fontSize: 'var(--font-size-xxs)', color: (platform.key === 'instagram' || platform.key === 'tiktok') ? 'var(--brand-white)' : 'var(--neutral-700)', cursor: 'pointer', fontWeight: 500 }}>
                <span>{platform.icon}</span>
                <span>{platform.prefix}{val}</span>
              </div>
            );
          })}
        </div>

      </div>

    ) : (
      /* ══ DESKTOP: existing horizontal layout ══ */
      <div style={{ display: 'flex', gap: 'var(--space-xxl)', alignItems: 'flex-start' }}>

        {/* Avatar */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <div style={{ width: 88, height: 88, borderRadius: 'var(--radius-circle)', overflow: 'hidden', background: 'var(--neutral-100)', border: '3px solid var(--feedback-warning-bg)' }}>
            {profile?.avatar_url
              ? <img src={profile.avatar_url} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-h2)', color: 'var(--neutral-300)' }}>📷</div>
            }
          </div>
          <label htmlFor="avatar-upload" style={{ position: 'absolute', bottom: 2, right: 2, width: 26, height: 26, borderRadius: 'var(--radius-circle)', background: 'var(--feedback-warning)', color: 'var(--brand-white)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.2)', fontSize: 'var(--font-size-base)' }}>
            {uploadingAvatar ? '…' : <IoCameraOutline />}
          </label>
          <input id="avatar-upload" type="file" accept="image/*" onChange={handleAvatarUpload} style={{ display: 'none' }} />
        </div>

        {/* Identity */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>

          {/* Row 1: username + location + archetype | share */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-md)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', fontWeight: 600 }}>@</span>
              <InlineEdit
                value={profile?.username}
                placeholder="username"
                onSave={val => updateProfile({ username: val.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                style={{ fontSize: 22, fontWeight: 800, color: 'var(--neutral-800)', fontFamily: 'var(--font-display)' }}
              />
              <InlineEdit
                value={profile?.location}
                placeholder="📍 location"
                prefix="📍 "
                onSave={val => updateProfile({ location: val })}
                style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)' }}
              />
              {profile?.archetype_label ? (
                <button onClick={() => setShowArchetypeModal(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '6px 14px', background: 'var(--neutral-800)', borderRadius: 'var(--radius-xxl)', border: 'none', cursor: 'pointer', transition: 'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#44403c'}
                  onMouseLeave={e => e.currentTarget.style.background = '#292524'}>
                  <span style={{ fontSize: 15 }}>{profile.archetype_emoji}</span>
                  <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--brand-paper)' }}>{profile.archetype_label}</span>
                </button>
              ) : (
                <button onClick={() => setShowArchetypeModal(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '6px 14px', background: 'var(--brand-paper)', borderRadius: 'var(--radius-xxl)', border: '1.5px dashed var(--neutral-300)', cursor: 'pointer', transition: 'all 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#d97706'; e.currentTarget.style.color = '#d97706'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#d6d3d1'; e.currentTarget.style.color = '#a8a29e'; }}>
                  <span style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-400)' }}>+ Archetype</span>
                </button>
              )}
            </div>
            <div style={{ flexShrink: 0 }}>
              {saveStatus && (
  <div
    // The error chip retries. It used to be a plain div that said "tap to
    // retry" and did nothing when tapped (audit 0.4).
    role={saveStatus === 'error' ? 'button' : 'status'}
    tabIndex={saveStatus === 'error' ? 0 : undefined}
    onClick={saveStatus === 'error' ? retryFailedSave : undefined}
    onKeyDown={saveStatus === 'error' ? (e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); retryFailedSave(); } }) : undefined}
    style={{
    cursor: saveStatus === 'error' ? 'pointer' : 'default',
    display: 'flex', alignItems: 'center', gap: 5,
    padding: '4px 10px', borderRadius: 'var(--radius-xxl)', fontSize: 'var(--font-size-xs)', fontWeight: 600,
    background: saveStatus === 'error' ? 'var(--feedback-error-bg)' : saveStatus === 'saving' ? 'var(--neutral-100)' : '#d1fae5',
    color: saveStatus === 'error' ? '#991b1b' : saveStatus === 'saving' ? 'var(--neutral-400)' : '#065f46',
    border: `1px solid ${saveStatus === 'error' ? '#fca5a5' : saveStatus === 'saving' ? '#e8e4dc' : '#a7f3d0'}`,
  }}>
    {saveStatus === 'saving' && <div style={{ width: 8, height: 8, borderRadius: 'var(--radius-circle)', border: '1.5px solid var(--neutral-400)', borderTopColor: 'transparent', animation: 'spin 0.7s linear infinite' }} />}
    {saveStatus === 'saved' && '✓'}
    {saveStatus === 'error' && '⚠'}
    {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved' : "Didn't save, tap to retry"}
  </div>
)}
          {guestHeaderFailed && (
  <div
    // The header edits made before sign-up, kept on screen until they save.
    role="button"
    tabIndex={0}
    onClick={retryGuestHeader}
    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); retryGuestHeader(); } }}
    style={{
      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, marginTop: 'var(--space-xs)',
      padding: '4px 10px', borderRadius: 'var(--radius-xxl)', fontSize: 'var(--font-size-xs)', fontWeight: 600,
      background: 'var(--feedback-error-bg)', color: '#991b1b', border: '1px solid #fca5a5',
    }}>
    ⚠ Your profile edits didn't save, tap to retry
  </div>
)}
              <ShareButton profile={profile} blocks={columns.flat()} />
            </div>
          </div>

          {/* Row 2: Genre pills */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', flexWrap: 'wrap', position: 'relative' }}>
            {userGenres.map(g => (
              <span key={g} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-xs)', padding: '4px 12px', background: 'var(--neutral-800)', color: 'var(--brand-paper)', borderRadius: 'var(--radius-xxl)', fontSize: 'var(--font-size-sm)', fontWeight: 500 }}>
                {g}
                <span onClick={() => removeGenre(g)} style={{ cursor: 'pointer', opacity: 0.5, fontSize: 'var(--font-size-xxs)', marginLeft: 'var(--space-xxs)', lineHeight: 1 }}>×</span>
              </span>
            ))}
            {userGenres.length < 3 && (
              <button onClick={() => setShowGenreDropdown(!showGenreDropdown)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-xs)', padding: '4px 12px', background: 'transparent', border: '1.5px dashed var(--neutral-300)', borderRadius: 'var(--radius-xxl)', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', cursor: 'pointer', transition: 'all 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#d97706'; e.currentTarget.style.color = '#d97706'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#d6d3d1'; e.currentTarget.style.color = '#a8a29e'; }}>
                + Genre
              </button>
            )}
            {showGenreDropdown && (
              <div ref={genreDropdownRef} style={{ position: 'absolute', top: '100%', left: 0, zIndex: 100, background: 'var(--brand-white)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--brand-sand)', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', width: 280, marginTop: 'var(--space-6)' }}>
                <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--neutral-100)' }}>
                  <input value={genreSearch} onChange={e => { setGenreSearch(e.target.value); if (genreWarning) setGenreWarning(''); }}
                    placeholder="Search or type a new genre..." autoFocus
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: 'var(--font-size-base)', color: 'var(--neutral-800)' }} />
                </div>
                <div style={{ maxHeight: 200, overflowY: 'auto', scrollbarWidth: 'none', padding: '6px 0' }}>
                  {canonicalGenres.length === 0 ? (
                    <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', padding: '10px 14px', margin: 'var(--space-none)', textAlign: 'center' }}>Loading genres...</p>
                  ) : filteredGenres.length > 0 ? (
                    filteredGenres.map(g => (
                      <button key={g} onClick={() => addGenre(g, false)}
                        style={{ display: 'block', width: '100%', padding: '8px 14px', background: 'none', border: 'none', textAlign: 'left', fontSize: 'var(--font-size-base)', color: 'var(--neutral-800)', cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#fef3c7'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                        {g}
                      </button>
                    ))
                  ) : genreSearch.trim() && !isCustomGenreInput ? (
                    <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', padding: '10px 14px', margin: 'var(--space-none)', fontStyle: 'italic' }}>Already added to your profile</p>
                  ) : !genreSearch.trim() ? (
                    <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', padding: '10px 14px', margin: 'var(--space-none)' }}>Start typing to search genres</p>
                  ) : null}
                </div>
                {isCustomGenreInput && (
                  <div style={{ padding: '10px 12px', borderTop: '1px solid var(--neutral-100)', background: 'var(--brand-paper)', borderRadius: '0 0 12px 12px' }}>
                    <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', margin: '0 0 6px', lineHeight: 1.4 }}>"{genreSearch.trim()}" isn't in our list yet</p>
                    <button onClick={() => addGenre(genreSearch.trim(), true)} disabled={addingCustomGenre}
                      style={{ width: '100%', padding: '8px 12px', background: addingCustomGenre ? 'var(--brand-sand)' : 'var(--neutral-800)', color: addingCustomGenre ? 'var(--neutral-400)' : 'var(--brand-paper)', border: 'none', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', fontWeight: 600, cursor: addingCustomGenre ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-6)' }}>
                      <span>{addingCustomGenre ? 'Adding...' : `+ Add "${genreSearch.trim()}"`}</span>
                      {!addingCustomGenre && <span style={{ fontSize: 'var(--font-size-xxs)', opacity: 0.55, fontWeight: 400 }}>· sent for review</span>}
                    </button>
                    {genreWarning && (
                      <p role="status" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--feedback-warning-ink)', margin: '8px 0 0', lineHeight: 1.4 }}>
                        {genreWarning}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Row 3: Social pills */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
            {SOCIAL_PLATFORMS.map(platform => {
              const val       = socialLinks[platform.key];
              const isEditing = editingSocial === platform.key;
              if (!val && !isEditing) {
                return (
                  <button key={platform.key} onClick={() => { setEditingSocial(platform.key); setSocialInput(''); }}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-xs)', padding: '5px 12px', background: 'transparent', border: '1px dashed var(--brand-sand)', borderRadius: 'var(--radius-xxl)', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', cursor: 'pointer', transition: 'all 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#d97706'; e.currentTarget.style.color = '#d97706'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#e8e4dc'; e.currentTarget.style.color = '#a8a29e'; }}>
                    {platform.icon} +{platform.label}
                  </button>
                );
              }
              if (isEditing) {
                return (
                  <div key={platform.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-6)', padding: '5px 12px', background: 'var(--feedback-warning-bg)', border: '1.5px solid var(--feedback-warning-border)', borderRadius: 'var(--radius-xxl)' }}>
                    <span style={{ fontSize: 'var(--font-size-sm)' }}>{platform.icon}</span>
                    <input autoFocus value={socialInput} onChange={e => setSocialInput(e.target.value)}
                      onBlur={() => saveSocial(platform.key, socialInput)}
                      onKeyDown={e => { if (e.key === 'Enter') saveSocial(platform.key, socialInput); if (e.key === 'Escape') setEditingSocial(null); }}
                      placeholder={platform.placeholder || "@handle"}
                      style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 'var(--font-size-sm)', color: 'var(--feedback-warning-ink)', width: platform.key === 'email' ? 180 : 100 }} />
                  </div>
                );
              }
              return (
                <div key={platform.key} className="social-pill"
                  onClick={() => { setEditingSocial(platform.key); setSocialInput(val); }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', background: 'var(--neutral-100)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-xxl)', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-700)', cursor: 'pointer', fontWeight: 500, transition: 'all 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#fef3c7'; e.currentTarget.style.borderColor = '#fde68a'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#f5f5f4'; e.currentTarget.style.borderColor = '#e8e4dc'; }}>
                  <span>{platform.icon}</span>
                  <span>{platform.prefix}{val}</span>
                </div>
              );
            })}
          </div>

        </div>
      </div>
    )}

  </div>
</div>

      {/* ── BLOCK GRID ── */}
{(isMobile && !forcePrintMode) ? (
  /* ── MOBILE: single column flat list ── */
  <div style={{ padding: '12px 16px 100px', maxWidth: PHONE_COLUMN_MAX, margin: '0 auto' }}>
{mobileBlocks.map(block => (
  <BlockWrapper
    key={block.id}
    block={block}
    profile={profile}
    onRemove={removeBlock}
    onDragStart={() => {}}
    onDragOver={() => {}}
    onDrop={() => {}}
    isDragging={false}
    onUpdate={updateProfile}
    onUpdateBlock={updateBlockData}
    onOpenCsvModal={() => setShowCsvModal(true)}
    csvRefreshKey={csvRefreshKey}
    pinned={block.type === 'journal'}
  />
))}
    <button
      onClick={() => setShowMobileBlockPicker(true)}
      style={{ position: 'fixed', bottom: 80, right: 20, width: 52, height: 52, borderRadius: 'var(--radius-circle)', background: 'var(--neutral-800)', color: 'var(--brand-white)', border: 'none', fontSize: 'var(--font-size-h4)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(0,0,0,0.2)', zIndex: 200, lineHeight: 1 }}>
      +
    </button>
  </div>
) : (
  /* ── DESKTOP or FORCE PRINT: 3-column + palette ── */
  <div style={{ maxWidth: 1200, margin: '0 auto', padding: '28px 32px', display: 'flex', gap: 'var(--space-xl)', alignItems: 'flex-start' }}>
    {!forcePrintMode && (
      <div data-no-print="true">
        <BlockPalette existingTypes={existingTypes} onDragStart={type => setDraggingPaletteType(type)} />
      </div>
    )}
   <div style={{ flex: 1 }}>
      {/* Fixture, not conditional -- journalBlock is never null (see its
          synthesis above), so this renders on every profile including one
          with zero stored blocks otherwise. First, per review: the hint
          below is a demo of dragging a block out of the palette, and must
          not sit ahead of the one thing that has to be first. */}
      <div style={{ marginBottom: 'var(--space-14)' }}>
        <BlockWrapper
          block={journalBlock}
          profile={profile}
          onRemove={removeBlock}
          onDragStart={() => {}}
          onDragOver={() => {}}
          onDrop={() => {}}
          isDragging={false}
          onUpdate={updateProfile}
          onUpdateBlock={updateBlockData}
          onOpenCsvModal={() => setShowCsvModal(true)}
          csvRefreshKey={csvRefreshKey}
          pinned
        />
      </div>
      {columns.flat().length === 0 && (
        <ProfileOnboardingHint userId={user?.id} />
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-14)', alignItems: 'start' }}>
        {columns.map((col, colIdx) => (
         <DropColumn
  key={colIdx}
  col={col}
  colIdx={colIdx}
  draggingPaletteType={draggingPaletteType}
  dragBlock={dragBlock}
  onAddBlock={addBlock}
  onRemoveBlock={removeBlock}
  onDragStartBlock={handleDragStart}
  onDropBlock={handleDrop}
  onDropFromPalette={(type, colIdx, insertAt) => { setDraggingPaletteType(null); addBlock(type, colIdx, insertAt); }}
  profile={profile}
  onUpdate={updateProfile}
  onUpdateBlock={updateBlockData}
  onOpenCsvModal={() => setShowCsvModal(true)}
  csvRefreshKey={csvRefreshKey}
  hideTypes={['journal']}
/>
        ))}
      </div>
    </div>
  </div>
)}

      {/* ── MODALS ── */}
      {showArchetypeModal && (
        <ArchetypeModal
          profile={profile}
          onSave={async archetype => {
            await updateProfile({ archetype_emoji: archetype.emoji, archetype_label: archetype.label });
            setShowArchetypeModal(false);
          }}
          onClose={() => setShowArchetypeModal(false)}
        />
      )}

      {showMobileBlockPicker && (
        <MobileBlockPicker
          existingTypes={existingTypes}
          addedBlocks={allBlocks.filter(b => b.type !== 'journal')}
          onAdd={type => addBlock(type, 0)}
          onMove={(blockId, dir) => moveBlockMobile(blockId, dir)}
          onClose={() => setShowMobileBlockPicker(false)}
        />
      )}

      <SignUpGate gate={gate} />
      {/* On a phone it sits above the floating + button (bottom 80, 52 tall),
          so the button stays reachable while the toast is up. */}
      {undoRemove && (
        <div role="status" data-no-print="true"
          style={{ position: 'fixed', left: '50%', bottom: isMobile ? 144 : 28, transform: 'translateX(-50%)', zIndex: 1200, display: 'flex', alignItems: 'center', gap: 'var(--space-md)', padding: '10px 12px 10px 16px', background: 'var(--neutral-900)', color: 'var(--brand-white)', borderRadius: 'var(--radius-xxl)', boxShadow: '0 8px 24px rgba(0,0,0,0.25)', fontSize: 'var(--font-size-sm)', maxWidth: 'min(480px, calc(100vw - 32px))' }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Removed “{undoRemove.label}”</span>
          <button onClick={undoRemoveBlock}
            style={{ flexShrink: 0, padding: '6px 12px', background: 'var(--brand-white)', color: 'var(--neutral-900)', border: 'none', borderRadius: 'var(--radius-xxl)', fontSize: 'var(--font-size-sm)', fontWeight: 700, cursor: 'pointer' }}>
            Undo
          </button>
        </div>
      )}
     {showCsvModal && (
 <CSVUploadModal
  onClose={() => setShowCsvModal(false)}
  onComplete={() => { setShowCsvModal(false); setCsvRefreshKey(k => k + 1); }}
/>
)}

<style>{`
  @keyframes spin        { to { transform: rotate(360deg); } }
  @keyframes slideUp     { from { transform: translateY(100%); } to { transform: translateY(0); } }
  @keyframes shareDropIn { from { opacity:0; transform:translateY(-6px) scale(0.97); } to { opacity:1; transform:translateY(0) scale(1); } }
  * { box-sizing: border-box; }
  ::-webkit-scrollbar { display: none; }

  @media print {
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    @page {
      margin: 0;
      size: 1100px auto;
    }
    body {
      margin: 0 !important;
      width: 1100px !important;
      min-width: 1100px !important;
    }
    [data-no-print="true"]   { display: none !important; }
    button:not(.social-pill) { display: none !important; }
    .block-wrapper           { break-inside: avoid; }
  }
`}</style>
    </div>
  );
}